import React, { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import { CONTACT, RDV, SERVICES } from "../config/routes";

import "./styles/Rdv.css";

const TZ = "Europe/Paris";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatDateLabel(date) {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(date);
}

function toISODate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function buildSlots({ days = 14 }) {
  const times = [
    "10:00", "11:00",
    "14:00", "15:00",
    "16:00", "17:00",
    "18:00", "19:00",
  ];

  const out = [];
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);

    const day = d.getDay();
    const isWeekend = day === 0 || day === 6;
    if (isWeekend) continue;

    const dateKey = toISODate(d);
    out.push({
      date: d,
      dateKey,
      label: formatDateLabel(d),
      times,
    });
  }

  return out;
}

export default function Rdv() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 599);

  const [days] = useState(() => buildSlots({ days: 18 }));
  const [selectedDayKey, setSelectedDayKey] = useState(days[0]?.dateKey || "");
  const [selectedTime, setSelectedTime] = useState("");

  const selectedDay = useMemo(
    () => days.find((d) => d.dateKey === selectedDayKey),
    [days, selectedDayKey]
  );

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [projectType, setProjectType] = useState("site_web");
  const [budget, setBudget] = useState("2_5k");
  const [timeline, setTimeline] = useState("asap");
  const [message, setMessage] = useState("");
  const [takenSlots, setTakenSlots] = useState({}); 

  const [website, setWebsite] = useState("");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();
    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }
    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".rdv");
    if (!mainContainer) return;
    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  const metaTitle = "Réserver un appel - Dreamer Agency";
  const metaDesc =
    "Réservez un appel (15–30 min) : cadrage, conseils, estimation. Choisissez un créneau, décrivez votre projet, confirmation par email.";
  const ogImage = "https://dreameragency.fr/media/OG/agency_background.png";

  const selectedLabel = useMemo(() => {
    if (!selectedDayKey || !selectedTime) return "";
    const d = days.find((x) => x.dateKey === selectedDayKey)?.date;
    if (!d) return "";
    return `${formatDateLabel(d)} • ${selectedTime} (${TZ})`;
  }, [days, selectedDayKey, selectedTime]);

  const refreshSlots = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) return;

      const from = days[0]?.dateKey;
      const to = days[days.length - 1]?.dateKey;
      if (!from || !to) return;

      const { data } = await axios.get(`${apiUrl}/call-booking-slots`, {
        params: { from, to },
      });

      const map = {};
      (data || []).forEach(({ dateKey, time }) => {
        if (!dateKey || !time) return;
        if (!map[dateKey]) map[dateKey] = {};
        map[dateKey][time] = true;
      });

      setTakenSlots(map);
    } catch (e) {
    }
  };

  useEffect(() => {
    refreshSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg("");

    if (website) {
      return;
    }
    if (!selectedDayKey || !selectedTime) {
      setErrorMsg("Choisissez un créneau avant d’envoyer.");
      return;
    }
    if (!fullName.trim() || !email.trim()) {
      setErrorMsg("Nom et email sont requis.");
      return;
    }

    if (takenSlots?.[selectedDayKey]?.[selectedTime]) {
      setErrorMsg("Ce créneau vient d’être pris. Choisissez-en un autre.");
      await refreshSlots();
      return;
    }

    setSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setErrorMsg("REACT_APP_API_URL manquant. Ajoutez-le pour activer l’envoi.");
        setSubmitting(false);
        return;
      }

      const body = [
        `📅 Créneau souhaité : ${selectedLabel}`,
        `👤 Nom : ${fullName}`,
        `✉️ Email : ${email}`,
        company ? `🏢 Société : ${company}` : "",
        `📌 Type : ${projectType}`,
        `💰 Budget : ${budget}`,
        `⏱️ Délai : ${timeline}`,
        message ? `📝 Message :\n${message}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      const resp = await axios.post(`${apiUrl}/call-booking`, {
        name: fullName,
        firstName: "-",
        email,
        phone: "-",
        contactOption: "callBooking",
        message: body,
        slotDate: selectedDayKey,
        slotTime: selectedTime,
        slotLabel: selectedLabel,

        company,
        projectType,
        budget,
        timeline,

        website,
      });

      if (resp?.data?.success) {
        setSuccess(true);
      } else {
        setErrorMsg("Impossible d’envoyer la demande. Réessayez ou utilisez Contact.");
      }

      setTakenSlots(prev => ({
      ...prev,
      [selectedDayKey]: { ...(prev[selectedDayKey] || {}), [selectedTime]: true }
    }));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setErrorMsg("Ce créneau a été réservé entre-temps. Choisissez-en un autre.");
        await refreshSlots();
      } else {
        setErrorMsg("Erreur lors de l’envoi. Réessayez ou utilisez Contact.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <main className={`rdv ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDesc} />
          <link rel="canonical" href={`https://dreameragency.fr${RDV}`} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDesc} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://dreameragency.fr${RDV}`} />
          <meta property="og:image" content={ogImage} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDesc} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>

        <section className="rdv-hero">
          <div className="rdv-container">
            <div className="rdv-success">
              <p className="rdv-eyebrow">Demande envoyée</p>
              <h1 className="rdv-h1">Demande reçue - confirmation du rendez-vous par email.</h1>
              <p className="rdv-muted">
                Créneau demandé : <strong>{selectedLabel}</strong>
                <br />
                Vous recevrez rapidement la confirmation et le lien visio (Google Meet).
              </p>

              <div className="rdv-successActions">
                <Link to={SERVICES} className="btn btn-secondary">Voir les services</Link>
                <Link to="/" className="btn btn-ghost">Retour accueil</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={`rdv ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={`https://dreameragency.fr${RDV}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dreameragency.fr${RDV}`} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <section className="rdv-hero">
        <div className="rdv-container">
          <div className="rdv-heroGrid">
            <div className="rdv-heroLeft">
              <p className="rdv-eyebrow">Réserver un appel</p>
              <h1 className="rdv-h1">Un échange simple pour cadrer votre besoin</h1>
              <p className="rdv-lead">
                15–30 minutes pour clarifier votre besoin, valider l’approche (web, SaaS, mobile ou automatisation)
                et repartir avec une estimation réaliste et les prochaines étapes.
              </p>

              <div className="rdv-perks">
                <div className="perk">
                  <span className="perk-top">Sans engagement</span>
                  <span className="perk-bot">cadrage & conseils</span>
                </div>
                <div className="perk">
                  <span className="perk-top">15–30 min</span>
                  <span className="perk-bot">Google Meet</span>
                </div>
                <div className="perk">
                  <span className="perk-top">Plan d’action</span>
                  <span className="perk-bot">priorités + prochaines étapes</span>
                </div>
              </div>

              <div className="rdv-miniNote">
                <strong>Informations demandées :</strong> un créneau et quelques éléments sur votre projet.
                <br />
                <span className="rdv-muted">La confirmation est ensuite envoyée par email avec le lien visio.</span>
              </div>

              <div className="rdv-altLink">
                <span className="rdv-muted">Vous préférez écrire ?</span>{" "}
                <Link to={CONTACT} className="link">Contact →</Link>
              </div>
            </div>

            <div className="rdv-heroRight">
              <div className="rdv-card">
                <p className="rdv-cardTitle">Choisissez un créneau</p>

                <div className="rdv-picker">
                  <div className="rdv-days">
                    {days.slice(0, 10).map((d) => (
                      <button
                        key={d.dateKey}
                        type="button"
                        className={`rdv-day ${d.dateKey === selectedDayKey ? "active" : ""}`}
                        onClick={() => {
                          setSelectedDayKey(d.dateKey);
                          setSelectedTime("");
                        }}
                      >
                        <span className="rdv-dayTop">{d.label}</span>
                        <span className="rdv-dayBot">Créneaux</span>
                      </button>
                    ))}
                  </div>

                  <div className="rdv-times">
                    <div className="rdv-timesHead">
                      <span className="rdv-muted">Heures disponibles</span>
                      <span className="rdv-muted">{TZ}</span>
                    </div>

                    <div className="rdv-timeGrid">
                      {(selectedDay?.times || []).map((time) => {
                        const isTaken = !!takenSlots?.[selectedDayKey]?.[time];
                        return (
                          <button
                            key={time}
                            type="button"
                            disabled={isTaken}
                            className={`rdv-time ${time === selectedTime ? "active" : ""} ${isTaken ? "taken" : ""}`}
                            onClick={() => {
                              if (isTaken) return;
                              setSelectedTime(time);
                            }}
                            title={isTaken ? "Créneau déjà pris" : ""}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>

                    {selectedLabel ? (
                      <div className="rdv-selected">
                        <span className="rdv-muted">Créneau sélectionné</span>
                        <strong>{selectedLabel}</strong>
                      </div>
                    ) : (
                      <div className="rdv-selected">
                        <span className="rdv-muted">Sélectionnez un jour et une heure</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rdv-card rdv-cardSoft">
                <p className="rdv-cardTitle">Ce que nous couvrons pendant l’appel</p>
                <ul className="rdv-list">
                  <li>Objectif et cible (résultat attendu)</li>
                  <li>Périmètre MVP, pages ou fonctionnalités prioritaires</li>
                  <li>Contraintes techniques éventuelles</li>
                  <li>Budget / délais réalistes</li>
                  <li>Prochaines étapes et plan d’exécution</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rdv-section">
        <div className="rdv-container">
          <div className="rdv-formGrid">
            <div className="rdv-formLeft">
              <h2 className="rdv-h2">Vos informations (30 secondes)</h2>
              <p className="rdv-muted">
                Plus le contexte est clair, plus la réponse pourra être précise et utile.
              </p>

              <form className="rdv-form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  autoComplete="off"
                  className="rdv-hp"
                  tabIndex={-1}
                />

                <div className="rdv-row">
                  <div className="field">
                    <label>Nom *</label>
                    <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Votre nom" />
                  </div>
                  <div className="field">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="rdv-row">
                  <div className="field">
                    <label>Société (optionnel)</label>
                    <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Nom de société" />
                  </div>
                  <div className="field">
                    <label>Type de projet</label>
                    <select value={projectType} onChange={(e) => setProjectType(e.target.value)}>
                      <option value="site_web">Site web</option>
                      <option value="saas">Web app / SaaS</option>
                      <option value="mobile">App mobile</option>
                      <option value="growth">Lancement / acquisition</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>
                </div>

                <div className="rdv-row">
                  <div className="field">
                    <label>Budget (approx.)</label>
                    <select value={budget} onChange={(e) => setBudget(e.target.value)}>
                      <option value="lt_2k">&lt; 2k€</option>
                      <option value="2_5k">2–5k€</option>
                      <option value="5_10k">5–10k€</option>
                      <option value="10_20k">10–20k€</option>
                      <option value="20k_plus">20k€+</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Délai</label>
                    <select value={timeline} onChange={(e) => setTimeline(e.target.value)}>
                      <option value="asap">Dès que possible</option>
                      <option value="1_2m">1–2 mois</option>
                      <option value="3m_plus">3+ mois</option>
                      <option value="explore">Exploration / cadrage</option>
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label>Contexte (optionnel mais utile)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ex: objectif, pages ou fonctionnalités, exemples, contraintes, existant…"
                    rows={5}
                  />
                </div>

                {errorMsg ? <div className="rdv-error">{errorMsg}</div> : null}

                <div className="rdv-submitRow">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? "Envoi..." : "Demander ce créneau"}
                  </button>
                  <span className="rdv-muted">
                    En envoyant, vous acceptez d’être recontacté par email pour confirmer le rendez-vous.
                  </span>
                </div>
              </form>
            </div>

            <div className="rdv-formRight">
              <div className="rdv-sideCard">
                <p className="rdv-cardTitle">Pourquoi ce cadrage est utile</p>
                <p className="rdv-muted">
                  Un échange court permet de clarifier rapidement les priorités :
                  objectif → périmètre → plan → estimation.
                </p>
                <div className="rdv-sideBullets">
                  <div className="sideBullet">
                    <span className="dot" />
                    <span>Vous savez quoi faire ensuite.</span>
                  </div>
                  <div className="sideBullet">
                    <span className="dot" />
                    <span>Vous évitez les mauvaises priorités.</span>
                  </div>
                  <div className="sideBullet">
                    <span className="dot" />
                    <span>Vous repartez avec une approche réaliste.</span>
                  </div>
                </div>
              </div>

              <div className="rdv-sideCard rdv-sideCardSoft">
                <p className="rdv-cardTitle">Besoin d’aller vite ?</p>
                <p className="rdv-muted">
                  Si vous avez une échéance courte, indiquez-la dans le message : nous proposerons l’approche la plus adaptée.
                </p>
                <Link className="btn btn-secondary" to={SERVICES}>
                  Voir les services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
